import { errorMessage } from "@atlas/shared";
import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Target, Check } from "lucide-react";
import { fetchGoals, createGoal } from "../api/goals";
import type { GoalTargetType } from "@atlas/shared";
import { Button } from "./Button";
import { Badge } from "./Badge";

interface GoalButtonProps {
  targetType: GoalTargetType;
  targetId: string | null | undefined;
  size?: "sm" | "md" | "lg";
}

function SetGoalDialog({
  targetType,
  targetId,
  onClose,
}: {
  targetType: GoalTargetType;
  targetId: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [targetDate, setTargetDate] = React.useState("");

  const mutation = useMutation({
    mutationFn: () =>
      createGoal({ targetType, targetId, targetDate: targetDate || undefined }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["goals"] });
      onClose();
    },
  });

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,.6)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          background: "var(--surface-card)",
          border: "1px solid var(--border-strong)",
          borderRadius: "var(--radius-xl)",
          padding: 28,
          maxWidth: 380,
          width: "90%",
          boxShadow: "var(--shadow-xl)",
        }}
      >
        <h2 style={{ font: "var(--type-h3)", color: "var(--text-primary)", marginBottom: 8 }}>
          Set a goal
        </h2>
        <p style={{ fontSize: 14, color: "var(--text-secondary)", marginBottom: 20, lineHeight: 1.5 }}>
          Track this on your dashboard until it&apos;s complete.
        </p>

        <label style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 20 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text-secondary)" }}>
            Target date
            <span style={{ fontWeight: 400, color: "var(--text-muted)", marginLeft: 4 }}>(optional)</span>
          </span>
          <input
            type="date"
            value={targetDate}
            onChange={(e) => setTargetDate(e.target.value)}
            style={{
              background: "var(--surface-sunken)",
              border: "1px solid var(--border-default)",
              borderRadius: "var(--radius-md)",
              padding: "8px 12px",
              fontSize: 14,
              color: "var(--text-primary)",
              outline: "none",
              width: "100%",
              boxSizing: "border-box",
            }}
          />
        </label>

        {mutation.isError && (
          <p style={{ fontSize: 13, color: "var(--status-danger)", marginBottom: 12 }}>
            {errorMessage(mutation.error, "Failed to set goal")}
          </p>
        )}

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending ? "Setting…" : "Set goal"}
          </Button>
        </div>
      </div>
    </div>
  );
}

export function GoalButton({ targetType, targetId, size = "md" }: GoalButtonProps) {
  const [dialogOpen, setDialogOpen] = React.useState(false);

  const goalsQuery = useQuery({
    queryKey: ["goals"],
    queryFn: fetchGoals,
  });

  const existing = goalsQuery.data?.goals.find(
    (g) => g.targetType === targetType && g.targetId === targetId,
  );

  if (existing?.completedAt) {
    return (
      <Badge variant="gold" dot>
        Goal completed
      </Badge>
    );
  }

  if (existing) {
    return (
      <Badge variant="success" icon={<Check size={12} />}>
        Goal set
      </Badge>
    );
  }

  return (
    <>
      <Button
        variant="secondary"
        size={size}
        leftIcon={<Target size={16} />}
        disabled={!targetId}
        onClick={() => setDialogOpen(true)}
      >
        Set a goal
      </Button>
      {dialogOpen && targetId && (
        <SetGoalDialog
          targetType={targetType}
          targetId={targetId}
          onClose={() => setDialogOpen(false)}
        />
      )}
    </>
  );
}
